import type { Module } from "vuex/types/index.d.ts";
import store from "@/store";
import useFormatter from "@/use/formatter";
import type { PaymentCalculationViewModel } from "@/models/viewModels";
import { $api } from "@/services/api";
import type { SelfSelectShowMyRates } from "@/models/selfSelectRates";

const { formatNumber } = useFormatter();

const pelRatesSelfSelectModule: Module<any, any> = {
  namespaced: true,
  state: {
    primaryApplicantSelectedCreditScoreRange: {},
    coApplicantSelectedCreditScoreRange: {},
    selectedCreditScoreRange: {},
    selectedRate: {} as any,
    currentApplicantTypeId: 0 as number,
    currentApplicantTypeName: "" as string,
    creditScoreRanges: [] as any[],
    fixedRateCreditScoreRanges: [] as any[],
    variableRateCreditScoreRanges: [] as any[],
    calculatedPaymentOptions: [] as PaymentCalculationViewModel[],
    calculatingEstimatedPayments: false as boolean,
    estimatedLoanAmount: 0 as number,
    showMyRatesResponse: {} as SelfSelectShowMyRates,
  },
  mutations: {
    SET_SHOW_MY_RATES_RESPONSE(state: any, response: any) {
      state.showMyRatesResponse = response;
    },
    SET_SELECTED_CREDIT_SCORE_RANGE(state: any, selectedCreditScoreRange: any) {
      state.selectedCreditScoreRange = selectedCreditScoreRange;
    },
    SET_SELECTED_RATE(state: any, selectedRate: any) {
      state.selectedRate = selectedRate;
    },
    SET_CURRENT_APPLICANT_TYPE_ID(state: any, currentApplicantTypeId: number) {
      state.currentApplicantTypeId = currentApplicantTypeId;
    },
    SET_CURRENT_APPLICANT_TYPE_NAME(state: any, currentApplicantTypeName: string) {
      state.currentApplicantTypeName = currentApplicantTypeName;
    },
    SET_CREDIT_SCORE_RANGES(state: any, creditScoreRanges: any) {
      state.creditScoreRanges = creditScoreRanges;
    },
    SET_FIXED_RATE_CREDIT_SCORE_RANGES(state: any, creditScoreRanges: any) {
      state.fixedRateCreditScoreRanges = creditScoreRanges;
    },
    SET_VARIABLE_RATE_CREDIT_SCORE_RANGES(state: any, creditScoreRanges: any) {
      state.variableRateCreditScoreRanges = creditScoreRanges;
    },
    RESET_ALL_STATES(state: any) {
      state.primaryApplicantSelectedCreditScoreRange = {};
      state.coApplicantSelectedCreditScoreRange = {};
      state.selectedCreditScoreRange = {};
      state.currentApplicantTypeId = 0;
      state.currentApplicantTypeName = "";
      state.creditScoreRanges = [];
      state.fixedRateCreditScoreRanges = [];
      state.variableRateCreditScoreRanges = [];
    },
  },
  actions: {
    setCreditScoreRanges({ commit }, payload) {
      commit("SET_CREDIT_SCORE_RANGES", payload);
    },
    async getSelfSelectRatesAsync({ commit, rootState }, applicantId: number) {
      const response = await $api.applications.getSelfSelectRates(rootState.appData?.application?.id, applicantId);

      if (response.success) {
        commit("SET_SHOW_MY_RATES_RESPONSE", response.results);
        commit("SET_CREDIT_SCORE_RANGES", response.results.creditScoreRanges);
      }

      return response;
    },
  },
  getters: {
    AvailableRateOptions: (state: any): any => {
      if (store?.state?.program?.pricingTiers?.length > 0) {
        return store?.state?.program?.pricingTiers
          .filter((i: any) => i.applicantType.id === state.currentApplicantTypeId)
          .filter((k: any) => k.ficoMin === state.selectedCreditScoreRange?.min)
          .map((option: any) => {
            // Fixed Rate
            if (option.rate.rateType.name === "Fixed") {
              const fixedInterestRate = option.rate.margin / 100;

              return {
                pricingTierId: option.id,
                max: option.ficoMax,
                min: option.ficoMin,
                term: option.term.months,
                termInYears: option.term.years,
                interestRate: fixedInterestRate,
                rate: fixedInterestRate * 100,
                rateDisplay: `${formatNumber(fixedInterestRate * 100)}%`,
                rateTypeName: option.rate.rateType.name,
              };
            } else if (option.rate.rateType.name === "Variable") {
              const indexRate = option.rate.indexType.rate;
              const variableRate = option.rate.margin + indexRate;

              let interestRate = 0 as number;
              if (variableRate < option.rate.floor) {
                interestRate = option.rate.floor;
              } else if (variableRate > option.rate.ceiling) {
                interestRate = option.rate.ceiling;
              } else {
                interestRate = variableRate;
              }

              return {
                pricingTierId: option.id,
                max: option.ficoMax,
                min: option.ficoMin,
                term: option.term.months,
                termInYears: option.term.years,
                interestRate: interestRate / 100,
                rate: interestRate,
                rateDisplay: `${formatNumber(interestRate)}%`,
                rateTypeName: option.rate.rateType.name,
              };
            } else
              return {
                pricingTierId: option.id,
                max: option.ficoMax,
                min: option.ficoMin,
                term: option.term.months,
                termInYears: option.term.years,
                interestRate: option.rate.margin / 100,
                rateTypeName: option.rate.rateType.name,
                rate: option.rate.margin / 100,
                rateDisplay: `${formatNumber(option.rate.margin / 100)}%`,
              };
          })
          .sort((a: any, b: any) => (a.termInYears > b.termInYears ? 1 : b.termInYears > a.termInYears ? -1 : 0))
          .sort((a: any, b: any) => (a.rateTypeName > b.rateTypeName ? 1 : b.rateTypeName > a.rateTypeName ? -1 : 0));
      }
      return [];
    },
  },
};
export default pelRatesSelfSelectModule;
